import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import AuthLayout from "@/components/layout/AuthLayout";
import { PasswordInput } from "@/components/common/PasswordInput";
import { FormAlert } from "@/components/common/Alert";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const COPY = {
  invite: { title: "Set your password", submit: "Set password and continue", path: "/auth/set-password" },
  reset: { title: "Choose a new password", submit: "Reset password", path: "/auth/reset-password" },
};

export default function TokenPassword({ mode }) {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [info, setInfo] = useState(null);
  const [invalid, setInvalid] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const copy = COPY[mode];

  useEffect(() => {
    if (!token) { setInvalid("This link is incomplete. Open it again from your email."); return; }
    api.post("/auth/token-info", { token, purpose: mode }).then(({ data }) => setInfo(data)).catch((e) => setInvalid(errorMessage(e)));
  }, [token, mode]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 12) return setError("Use at least 12 characters.");
    if (password !== confirm) return setError("The two passwords do not match.");
    setBusy(true);
    try {
      const { data } = await api.post(copy.path, { token, password });
      if (mode === "invite") {
        setUser(data);
        navigate("/welcome", { replace: true });
      } else {
        toast.success(data.message);
        navigate("/signin", { replace: true });
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (invalid) {
    return (
      <AuthLayout title={copy.title} testid={`${mode}-password-page`}>
        <FormAlert testid="token-invalid">{invalid}</FormAlert>
        <p className="mt-4 text-sm text-muted-foreground">
          {mode === "invite" ? "Ask the hub owner to send you a new invitation." : (
            <>Request a new link from <Link to="/forgot-password" className="font-medium text-foreground underline underline-offset-4" data-testid="token-invalid-forgot-link">Forgotten your password</Link>.</>
          )}
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={copy.title} subtitle={info ? `For ${info.name}, ${info.email}` : " "} testid={`${mode}-password-page`}>
      {!info ? (
        <div className="space-y-4"><Skeleton className="h-10 rounded-xl" /><Skeleton className="h-10 rounded-xl" /></div>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <FormAlert testid="token-password-error">{error}</FormAlert>
          <PasswordInput id="new-password" label="New password" value={password} onChange={setPassword} autoComplete="new-password" hint="At least 12 characters. Avoid common passwords." testid="token-password-input" />
          <PasswordInput id="confirm-password" label="Type it again" value={confirm} onChange={setConfirm} autoComplete="new-password" testid="token-password-confirm-input" />
          <Button type="submit" className="w-full" disabled={busy || !password || !confirm} data-testid="token-password-submit-button">
            {busy ? "Saving" : copy.submit}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
